import type { MapStateToPropsFactory } from 'react-redux';
import { connect } from 'react-redux';
import { logAnalytics } from 'shared/middlewares/analytics/logger';
import type { LiveForm } from 'state/secondaryMarket/live/form/input/inputForm.model';
import { liveKycSelectors } from 'state/secondaryMarket/live/kyc/kyc.selectors';
import type { LiveStatus } from 'state/secondaryMarket/live/live.model';
import { liveSelectors } from 'state/secondaryMarket/live/live.selector';
import {
  secondaryMarketLiveQuoteCancelRequestedThunk,
  secondaryMarketLiveQuoteRequestedThunk,
} from 'state/secondaryMarket/live/quote/liveQuote.thunk';
import type { SecondaryMarketFormError } from 'state/secondaryMarket/shared/form/validationResults';
import type { KycDetail, KycScoreStatus } from 'state/secondaryMarket/shared/kyc/kyc.model';
import { getUserFrontRights } from 'state/selectors';
import type { ThunkDispatch } from 'state/state.common';
import type { AppState } from 'state/state.model';

type OwnProps = Readonly<{
  tabId: string;
}>;

type StateProps = Readonly<{
  errors: readonly SecondaryMarketFormError<LiveForm>[]; //
  status: LiveStatus; //
  errorMessage?: string; //
  kycScore: KycScoreStatus | undefined; //
  kycDetails: readonly KycDetail[] | undefined; //
  timeout?: number;
  canQuote?: boolean;
  backendReports: readonly SecondaryMarketFormError<LiveForm>[];
}>;

type DispatchProps = Readonly<{
  onQuote: () => void;
  onDone: (closeTab?: boolean, isCancel?: boolean) => void;
}>;

export type LiveSidePanelProps = OwnProps & StateProps & DispatchProps;

const mapStateToProps: MapStateToPropsFactory<StateProps, OwnProps, AppState> = () => {
  const getValidationErrors = liveSelectors.makeGetValidationErrors();
  const getBackendReports = liveSelectors.makeGetBackendReports();
  return (state, { tabId }): StateProps => {
    const status = liveSelectors.getStatus(state, tabId);
    const errors = getValidationErrors(state, tabId);
    const kycScore = liveKycSelectors.getKycStatusScore(state, tabId);
    const kycDetails = liveKycSelectors.getKycStatusDetails(state, tabId);
    const errorMessage = liveSelectors.getErrorMessage(state, tabId);
    const timeout = liveSelectors.getTimeout(state, tabId);
    const canQuote = getUserFrontRights(state).secondaryQuoteEnabled;
    return {
      errors,
      status,
      errorMessage,
      kycScore,
      kycDetails,
      timeout,
      canQuote,
      backendReports: getBackendReports(state, tabId),
    };
  };
};

const mapDispatchToProps = (dispatch: ThunkDispatch, { tabId }: OwnProps): DispatchProps => {
  return {
    onQuote: () => {
      logAnalytics.trackEvent('Secondary market', 'Request for live quote');
      dispatch(secondaryMarketLiveQuoteRequestedThunk(tabId));
    },
    onDone: (closeTab, isCancel) => {
      if (isCancel) {
        logAnalytics.trackEvent('Secondary market', 'Cancel quote');
      }
      dispatch(secondaryMarketLiveQuoteCancelRequestedThunk(tabId, closeTab, isCancel));
    },
  };
};

export const connectSidePanel = connect(mapStateToProps, mapDispatchToProps);
